/**
 * Investigação como estado da URL.
 *
 * O que separa "ler um evento" de "investigar" é o pivô: um valor vira um link
 * que abre a lista já filtrada (§9). Para o operador colar esse endereço num
 * ticket e o colega abrir a MESMA tela (§7), o recorte precisa morar na URL —
 * não em estado de componente que o F5 zera.
 *
 * Este módulo é a única fonte da tradução entre a query string e o recorte
 * `LogHistoryQuery`. Só o transporte: quem valida escopo é o servidor (400) e a
 * interface previne (§13); aqui apenas lê e escreve chaves.
 */

import { query } from '../api/client.ts'

/**
 * Os filtros de investigação que vivem na URL da tela de histórico.
 *
 * `endpointId` fica FORA: é o segmento de rota (`/logs/:endpointId`), não um
 * parâmetro. `cursor` e `pageSize` também: são efêmeros e opacos, não
 * compartilháveis. Tudo aqui é opcional e combinável (§12).
 */
export interface LogInvestigation {
  tenantId?: string
  eventType?: string
  eventAction?: string
  userName?: string
  processEntityId?: string
  relatedIp?: string
  relatedUser?: string
  relatedHash?: string
  source?: string
  nativeId?: string
  /** Módulo de origem (`etw`, `ebpf`, `journald`…). Exige escopo (§5). */
  eventModule?: string
  /** Nome curto do processo (`process_name`). Exige escopo (§5). */
  processName?: string
  /** Nome do serviço/unidade. Exige escopo (§5). */
  serviceName?: string
  /** Extensão do arquivo, com ou sem ponto — o servidor normaliza. Exige escopo (§5). */
  fileExtension?: string
  /** Consulta DNS — igualdade exata, não substring. Pivô de frota, sem escopo (§12.4). */
  dnsQuestion?: string
  from?: string
  to?: string
}

const KEYS = [
  'tenantId',
  'eventType',
  'eventAction',
  'userName',
  'processEntityId',
  'relatedIp',
  'relatedUser',
  'relatedHash',
  'source',
  'nativeId',
  'eventModule',
  'processName',
  'serviceName',
  'fileExtension',
  'dnsQuestion',
  'from',
  'to',
] as const

/** Lê o recorte da query string, omitindo chaves ausentes ou vazias. */
export function readInvestigation(search: URLSearchParams): LogInvestigation {
  const out: LogInvestigation = {}
  for (const key of KEYS) {
    const value = search.get(key)
    if (value !== null && value !== '') out[key] = value
  }
  return out
}

/** Há algum filtro de investigação ativo (fora o escopo de empresa). */
export function hasInvestigation(inv: LogInvestigation): boolean {
  return KEYS.some((key) => key !== 'tenantId' && inv[key] !== undefined)
}

/**
 * Os pivôs de fan-out e de processo — os alvos de "investigar com um clique".
 *
 * São os que aparecem como chip no banner de investigação, porque cada um
 * responde a uma pergunta inteira do §1 (quem falou com este IP, todo host que
 * viu este binário, o que este processo fez) e o operador chegou até eles por um
 * clique, não digitando. `eventAction`/`userName` ficam de fora: são filtros que
 * se digitam na barra, não pivôs.
 */
const PIVOT_KEYS = ['processEntityId', 'relatedIp', 'relatedUser', 'relatedHash'] as const

export interface PivotChip {
  key: (typeof PIVOT_KEYS)[number]
  label: string
  value: string
}

const PIVOT_LABEL: Record<(typeof PIVOT_KEYS)[number], string> = {
  processEntityId: 'Processo',
  relatedIp: 'IP',
  relatedUser: 'Usuário',
  relatedHash: 'Hash (IOC)',
}

/** Os pivôs ativos, para o banner desenhar e oferecer o "limpar". */
export function activePivots(inv: LogInvestigation): PivotChip[] {
  const out: PivotChip[] = []
  for (const key of PIVOT_KEYS) {
    const value = inv[key]
    if (value !== undefined) out.push({ key, label: PIVOT_LABEL[key], value })
  }
  return out
}

/** `/logs?…` a partir de um recorte. `query()` já omite vazios. */
export function logsHref(inv: LogInvestigation): string {
  return `/logs${query({ ...inv })}`
}

/** `/logs/:endpointId?…` — investigação escopada a um agente (antes/depois, timeline). */
export function agentHref(endpointId: string, inv: LogInvestigation): string {
  return `/logs/${encodeURIComponent(endpointId)}${query({ ...inv })}`
}

/** `/processes/:entityId` — árvore e linha do tempo de um processo (§12). */
export function processHref(entityId: string): string {
  return `/processes/${encodeURIComponent(entityId)}`
}

/**
 * Os construtores de pivô: cada um começa uma investigação NOVA, na frota
 * inteira (§9–§11).
 *
 * Não carregam o escopo anterior de propósito: "todo host que viu este binário"
 * (§11) perderia o sentido preso a um tenant. Combinar filtros para estreitar é
 * papel da barra de busca, que os funde na URL (§12).
 */
export const pivotHref = {
  /** A página do processo: árvore + linha do tempo (§12). */
  process: (id: string): string => processHref(id),
  /** Os eventos do processo no histórico, como filtro (§5). */
  processEvents: (id: string): string => `/logs${query({ processEntityId: id })}`,
  relatedIp: (ip: string): string => `/logs${query({ relatedIp: ip })}`,
  relatedUser: (user: string): string => `/logs${query({ relatedUser: user })}`,
  relatedHash: (hash: string): string => `/logs${query({ relatedHash: hash })}`,
  /** "Todo host que resolveu este domínio" — pivô de frota, sem escopo (§12.4). */
  dnsQuestion: (name: string): string => `/logs${query({ dnsQuestion: name })}`,
}
